import { useState } from "react";
import { Shield, ChevronRight, Sparkles, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import { downloadChairBriefPdf } from "../../services/businessDashApi";

/**
 * GET /api/admin-dash/chair-brief/ — default { visible: false } keeps the banner hidden.
 * When visible is true: title, summary, highlights[], pdf_export_available.
 * PDF: GET /api/admin-dash/chair-brief/pdf/ (Bearer + blob download).
 */
const ChairBriefBanner = ({ briefRaw, loading }) => {
  const [open, setOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  if (loading) {
    return (
      <p className="text-xs text-muted-foreground rounded-md border border-border/50 bg-muted/10 px-3 py-2">
        Loading Chair Brief…
      </p>
    );
  }

  const d = briefRaw?.data ?? briefRaw;
  if (!d || typeof d !== "object") return null;

  if (d.visible !== true) {
    return null;
  }

  const headline = d.title ?? d.headline ?? "Chair Brief – What matters now";
  const summary = d.summary ?? d.body ?? "";
  const highlights = Array.isArray(d.highlights) ? d.highlights : [];
  const pdfAvailable = d.pdf_export_available === true;
  const briefId = d.brief_id ?? d.id ?? null;

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadChairBriefPdf({ briefId });
      toast.success("Chair Brief PDF downloaded");
    } catch (e) {
      toast.error(e?.message || "Could not download PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "w-full group flex items-center gap-4 p-5 rounded-xl text-left",
          "bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-900/40 dark:to-slate-800/30",
          "border border-slate-200/60 dark:border-border hover:border-primary/30",
          "transition-all duration-200 hover:shadow-md",
        )}
      >
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-primary/10 text-primary">
          <Shield className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-foreground">{headline}</h3>
            {d.is_new === true && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                <Sparkles className="w-3 h-3" />
                New
              </span>
            )}
          </div>
          {summary ? (
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{summary}</p>
          ) : (
            <p className="text-sm text-muted-foreground mt-0.5">Open for details</p>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{headline}</DialogTitle>
            {d.round_date_range ? (
              <DialogDescription>{d.round_date_range}</DialogDescription>
            ) : null}
          </DialogHeader>
          {summary ? (
            <p className="text-sm text-foreground/90 whitespace-pre-wrap">{summary}</p>
          ) : (
            <p className="text-sm text-muted-foreground">No summary provided.</p>
          )}
          {highlights.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-foreground/80 space-y-1">
              {highlights.map((b, i) => (
                <li key={i}>{typeof b === "string" ? b : b?.text ?? String(b)}</li>
              ))}
            </ul>
          )}
          <div className="flex flex-col gap-2 pt-2">
            {pdfAvailable ? (
              <Button
                type="button"
                variant="outline"
                disabled={pdfLoading}
                className="w-full"
                onClick={handleDownloadPdf}
              >
                {pdfLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Download Chair Brief (PDF)
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground text-center">
                PDF export is not available for this brief.
              </p>
            )}
            <Button type="button" onClick={() => setOpen(false)} className="w-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChairBriefBanner;
